import {
  ActionMenu,
  DataGrid,
  DefinitionsTooltip,
  FilterToolbar,
  formatDate,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  SSOErrorsErrorType,
  useGetAdminPlanOptions,
  useGetAdminSsoErrors,
  usePatchAdminSsoError,
  type GetAdminSsoErrorsParams,
  type SsoErrorRowRes
} from '@ijoin/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Stack, Typography } from '@mui/material';
import {
  GridActionsCellItem,
  GridColDef,
  type GridRenderCellParams
} from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { useState } from 'react';

import {
  humanizeErrorType,
  LinkButton,
  SsoErrorDetailsModal
} from '../components';
import { useAdvisoryOrganizationOptions } from '../components/useAdvisoryOrganizationOptions';

const ERROR_TYPE_OPTIONS = Object.values(SSOErrorsErrorType).map(value => ({
  label: humanizeErrorType(value),
  value
}));

const ERROR_TYPE_GUIDE: Record<SSOErrorsErrorType, string> = {
  [SSOErrorsErrorType.ACCOUNT_ID_MISMATCH]:
    "The sso payload's account id does not match the organization's sso configuration.",
  [SSOErrorsErrorType.MISSING_TOKEN]:
    'The launch request arrived without an sso token.',
  [SSOErrorsErrorType.NO_SSO_CONFIG]:
    'No sso configuration exists for the organization.',
  [SSOErrorsErrorType.NOT_ENROLLED_IN_PLAN]:
    'The participant exists but has no enrollment in the target plan.',
  [SSOErrorsErrorType.OIDC_HANDSHAKE_FAILED]:
    'The OIDC handshake with the record keeper failed.',
  [SSOErrorsErrorType.PARTICIPANT_INFO_MISMATCH]:
    'Record-keeper data did not match the participant, such as a missing ssn.',
  [SSOErrorsErrorType.PARTICIPANT_NOT_FOUND]:
    'No participant or login matched the sso payload.',
  [SSOErrorsErrorType.PLAN_NOT_IN_SYSTEM]:
    'The external plan id is not in the system.',
  [SSOErrorsErrorType.RECORDKEEPER_ERROR]:
    'The record keeper returned an error during sso.',
  [SSOErrorsErrorType.RECORDKEEPER_TIMEOUT]:
    'The record keeper timed out during sso.',
  [SSOErrorsErrorType.SAML_VALIDATION_FAILED]:
    'The SAML assertion failed validation.',
  [SSOErrorsErrorType.UNKNOWN]: 'Legacy or unclassified sso failure.',
  [SSOErrorsErrorType.VALIDATION_FAILED]:
    'The sso launch request failed validation before reaching the record keeper.',
  [SSOErrorsErrorType.WRONG_INTEGRATION_TYPE]:
    'The integration does not support sso launch.'
};

const renderParticipantLink = (
  params: GridRenderCellParams<SsoErrorRowRes>
) => {
  const planEnrollmentId = params.row.Participants?.PlanEnrollments.find(
    enrollment => enrollment.planId === params.row.planId
  )?.planEnrollmentId;

  return params.row.advisoryOrganizationId &&
    params.row.planId &&
    planEnrollmentId ? (
    <LinkButton
      data-component='link'
      data-testid={`${params.field}-${params.row.ssoErrorId}`}
      params={{
        advisoryOrganizationId: params.row.advisoryOrganizationId,
        planEnrollmentId,
        planId: params.row.planId
      }}
      to='/organizations/$advisoryOrganizationId/plans/$planId/participants/$planEnrollmentId'
      variant='text'>
      {params.value}
    </LinkButton>
  ) : (
    (params.value ?? '')
  );
};

const SEARCH_QUERY_KEY = '/api/v3/admin/sso-errors';

const SsoErrorsScreen = () => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState<GridQuery>();
  const [selected, setSelected] = useState<null | SsoErrorRowRes>(null);
  const [draftOrgId, setDraftOrgId] = useState<null | string>();

  const errors = useGetAdminSsoErrors(query as GetAdminSsoErrorsParams, {
    query: { enabled: !!query, placeholderData: keepPreviousData }
  });

  const appliedOrgId = (query as GetAdminSsoErrorsParams | undefined)
    ?.advisoryOrganizationId;

  const orgs = useAdvisoryOrganizationOptions();

  const plansOrgId =
    draftOrgId === undefined ? appliedOrgId : (draftOrgId ?? undefined);

  const plans = useGetAdminPlanOptions(
    { ...(plansOrgId && { advisoryOrganizationId: plansOrgId }) },
    {
      query: {
        select: res =>
          res.map(plan => ({
            label: plan.description ?? plan.planId,
            value: plan.planId
          }))
      }
    }
  );

  const patchError = usePatchAdminSsoError({
    mutation: {
      onError: () => notifications.error('Failed to update SSO error'),
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
        notifications.success(
          variables.data.cleared ? 'Marked as cleared' : 'Marked as uncleared'
        );
      }
    }
  });

  const onCloseDetails = () => setSelected(null);

  const filterDefs: FilterDef[] = [
    {
      column: 'organizationName',
      isLoading: orgs.isLoading,
      label: 'Advisory Organization',
      name: 'advisoryOrganizationId',
      options: orgs.data ?? [],
      type: 'autocomplete'
    },
    {
      column: 'planName',
      isLoading: plans.isFetching,
      label: 'Plan',
      name: 'planId',
      options: plans.data ?? [],
      type: 'autocomplete'
    },
    { label: 'External Plan ID', name: 'externalPlanId', type: 'text' },
    { label: 'First Name', name: 'firstName', type: 'text' },
    { label: 'Last Name', name: 'lastName', type: 'text' },
    {
      label: 'Error Type',
      name: 'errorType',
      options: ERROR_TYPE_OPTIONS,
      type: 'select'
    },
    { label: 'Message', name: 'message', type: 'text' },
    {
      column: 'createdAt',
      endName: 'endCreatedAt',
      label: 'Created At',
      startName: 'startCreatedAt',
      type: 'dateRange'
    },
    {
      column: 'clearedByDate',
      endName: 'endClearedByDate',
      label: 'Cleared Date',
      startName: 'startClearedByDate',
      type: 'dateRange'
    },
    { label: 'Cleared', name: 'cleared', type: 'switch' }
  ];

  const columns: GridColDef<SsoErrorRowRes>[] = [
    {
      align: 'center',
      field: 'info',
      getActions: params => [
        <GridActionsCellItem
          data-testid={`info-${params.row.ssoErrorId}`}
          icon={<InfoOutlinedIcon />}
          key='details'
          label='Details'
          onClick={() => setSelected(params.row)}
        />
      ],
      headerName: '',
      sortable: false,
      type: 'actions',
      width: 50
    },
    {
      field: 'organizationName',
      headerName: 'Advisory Organization',
      renderCell: params =>
        params.row.advisoryOrganizationId ? (
          <LinkButton
            data-component='link'
            data-testid={`organization-${params.row.ssoErrorId}`}
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId
            }}
            to='/organizations/$advisoryOrganizationId'
            variant='text'>
            {params.row.AdvisoryOrganizations?.name ?? ''}
          </LinkButton>
        ) : (
          (params.row.AdvisoryOrganizations?.name ?? '')
        ),
      sortable: false,
      width: 200
    },
    {
      field: 'planName',
      headerName: 'Plan',
      renderCell: params =>
        params.row.advisoryOrganizationId && params.row.planId ? (
          <LinkButton
            data-component='link'
            data-testid={`plan-${params.row.ssoErrorId}`}
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId'
            variant='text'>
            {params.row.Plans?.description ?? ''}
          </LinkButton>
        ) : (
          (params.row.Plans?.description ?? '')
        ),
      sortable: false,
      width: 200
    },
    {
      field: 'externalPlanId',
      flex: 1,
      headerName: 'External Plan ID',
      minWidth: 150,
      sortable: false
    },
    {
      field: 'firstName',
      flex: 1,
      headerName: 'First Name',
      minWidth: 120,
      renderCell: renderParticipantLink,
      valueGetter: (_value, row) => row.Participants?.firstName ?? ''
    },
    {
      field: 'lastName',
      flex: 1,
      headerName: 'Last Name',
      minWidth: 140,
      renderCell: renderParticipantLink,
      valueGetter: (_value, row) => row.Participants?.lastName ?? ''
    },
    {
      field: 'errorType',
      flex: 1.25,
      headerName: 'Error Type',
      minWidth: 180,
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Error Type</span>
          <DefinitionsTooltip
            data-testid='errorTypeGuide'
            definitions={ERROR_TYPE_OPTIONS.map(({ label, value }) => ({
              definition: ERROR_TYPE_GUIDE[value],
              label
            }))}
            title='Error type reference'
          />
        </Stack>
      ),
      valueFormatter: value => humanizeErrorType(value)
    },
    {
      field: 'message',
      flex: 1.5,
      headerName: 'Message',
      minWidth: 240,
      sortable: false
    },
    {
      field: 'createdAt',
      flex: 1,
      headerName: 'Created At',
      minWidth: 180,
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MMM D, YYYY h:mm A' }) : ''
    },
    {
      field: 'clearedByName',
      flex: 1,
      headerName: 'Cleared By',
      minWidth: 150,
      valueGetter: (_value, row) =>
        row.ClearedBy
          ? `${row.ClearedBy.firstName} ${row.ClearedBy.lastName}`
          : ''
    },
    {
      field: 'clearedByDate',
      flex: 1,
      headerName: 'Cleared Date',
      minWidth: 180,
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MMM D, YYYY h:mm A' }) : ''
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <ActionMenu
          actions={[
            {
              content: params.row.clearedByLoginId
                ? 'Mark as uncleared'
                : 'Mark as cleared',
              onSelect: () =>
                patchError.mutate({
                  data: { cleared: !params.row.clearedByLoginId },
                  ssoErrorId: params.row.ssoErrorId
                })
            }
          ]}
          trigger={
            <IconButton
              data-testid={`actions-${params.row.errorType}-${params.row.ssoErrorId}`}>
              <MoreVertIcon />
            </IconButton>
          }
        />
      ),
      sortable: false,
      width: 64
    }
  ];

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>SSO Errors</Typography>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filterDefs}
        getRowId={row => row.ssoErrorId}
        initialSort={[{ field: 'createdAt', sort: 'desc' }]}
        loading={!query || errors.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{
          left: ['info', 'organizationName', 'planName'],
          right: ['actions']
        }}
        rowCount={errors.data?.pagination?.total ?? 0}
        rows={errors.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: { 'data-component': 'dataGrid', 'data-testid': 'ssoErrors' },
          toolbar: {
            onDraftChange: values =>
              setDraftOrgId((values.advisoryOrganizationId as string) || null),
            onRefresh: errors.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'ssoErrors-FilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <SsoErrorDetailsModal onClose={onCloseDetails} ssoError={selected} />
    </Stack>
  );
};

export const Route = createFileRoute('/sso-errors')({
  component: SsoErrorsScreen
});
